import { api } from "@/app/api";
import type {
  AdminNoticeInput,
  Campaign,
  CampaignDetail,
  CampaignPage,
  CampaignQuery,
  InboxPage,
  InboxQuery,
  NotificationQueued,
  PlayerPushInput,
} from "@/types/api";

/**
 * Notifications: sending, campaign history, and your own inbox.
 *
 * Three groups, gated differently:
 *
 *   - **Sends.** `notification.send` for player push, `admin.notice.send` for
 *     an internal notice. Both answer `202` — the campaign is queued, not
 *     delivered — so the panel says "Queued" and polls the campaign.
 *   - **Campaigns.** `notification.view`. Both flows share one history.
 *   - **Inbox.** No permission: it is the caller's own mail.
 *
 * Times are in the request's zone, which `X-Timezone` sets on every request
 * (see `lib/timezone`). Scheduling input is `scheduled_at_local`, the datetime
 * picker's raw value with no offset; never `toISOString()` it. Responses carry
 * every instant twice — `*_at` in UTC, `*_at_local` as ISO with an offset —
 * and the panel formats `*_at`.
 */
export const notificationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    sendPlayerPush: builder.mutation<NotificationQueued, PlayerPushInput>({
      query: (body) => ({ url: "/notifications/player-push", method: "POST", body }),
      invalidatesTags: ["Notification", "AuditLog"],
    }),

    sendAdminNotice: builder.mutation<NotificationQueued, AdminNoticeInput>({
      query: (body) => ({ url: "/notifications/admin-notice", method: "POST", body }),
      invalidatesTags: ["Notification", "AuditLog"],
    }),

    listCampaigns: builder.query<CampaignPage, CampaignQuery | void>({
      query: (params) => ({ url: "/notifications/campaigns", params: params ?? undefined }),
      providesTags: ["Notification"],
    }),

    /**
     * One campaign plus its delivery breakdown. Poll it (`pollingInterval`)
     * while `status` is `queued` or `sending`; stop at a terminal status.
     */
    getCampaign: builder.query<CampaignDetail, string>({
      query: (id) => `/notifications/campaigns/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Notification" as const, id }],
    }),

    /** Only `scheduled` and `queued` can be cancelled; anything else is a 409. */
    cancelCampaign: builder.mutation<Campaign, string>({
      query: (id) => ({ url: `/notifications/campaigns/${id}/cancel`, method: "POST" }),
      invalidatesTags: (_result, _error, id) => [
        "Notification",
        { type: "Notification" as const, id },
        "AuditLog",
      ],
    }),

    // --- Inbox -------------------------------------------------------------

    listInbox: builder.query<InboxPage, InboxQuery | void>({
      query: (params) => ({ url: "/notifications/inbox", params: params ?? undefined }),
      providesTags: [{ type: "Notification", id: "INBOX" }],
      // Every page carries the inbox-wide unread count. Copying it into the
      // badge's cache means opening the menu can never show an unread row under
      // a bell that says there is nothing new.
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            notificationsApi.util.upsertQueryData("getInboxUnreadCount", undefined, {
              unread_count: data.unread_count,
            }),
          );
        } catch {
          // The list shows its own error; the badge keeps its last count.
        }
      },
    }),

    /** Just the number. Cheap — poll this for the badge, not `/inbox`. */
    getInboxUnreadCount: builder.query<{ unread_count: number }, void>({
      query: () => "/notifications/inbox/unread-count",
      providesTags: [{ type: "Notification", id: "INBOX" }],
    }),

    /** 204. Idempotent — a second call does not move `read_at`. */
    markInboxRead: builder.mutation<void, string>({
      query: (id) => ({ url: `/notifications/inbox/${id}/read`, method: "PATCH" }),
      invalidatesTags: [{ type: "Notification", id: "INBOX" }],
    }),

    /** `{ read }` — how many were actually unread. Zeroes the badge optimistically. */
    /** 204. Registers this browser's FCM token for desktop alerts. Idempotent. */
    registerPushToken: builder.mutation<void, string>({
      query: (token) => ({ url: "/notifications/push-token", method: "PUT", body: { token } }),
    }),

    /** Removes this browser's FCM token, so pushes stop reaching it. */
    unregisterPushToken: builder.mutation<void, string>({
      query: (token) => ({
        url: "/notifications/push-token",
        method: "DELETE",
        body: { token },
      }),
    }),

    markAllInboxRead: builder.mutation<{ read: number }, void>({
      query: () => ({ url: "/notifications/inbox/read-all", method: "POST" }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          notificationsApi.util.updateQueryData("getInboxUnreadCount", undefined, (draft) => {
            draft.unread_count = 0;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [{ type: "Notification", id: "INBOX" }],
    }),
  }),
});

export const {
  useSendPlayerPushMutation,
  useSendAdminNoticeMutation,
  useListCampaignsQuery,
  useGetCampaignQuery,
  useCancelCampaignMutation,
  useListInboxQuery,
  useGetInboxUnreadCountQuery,
  useMarkInboxReadMutation,
  useMarkAllInboxReadMutation,
  useRegisterPushTokenMutation,
  useUnregisterPushTokenMutation,
} = notificationsApi;
